import { UnixTimestamp } from "@shared/types/common";

export enum SubscriptionPlanType {
  BASIC = 1,
  BEGINNER = 2,
  INTERMEDIATE = 3,
  PRO = 4,
  FREE = 5,
  STARTER = 6,
  ADVANCED = 7,
  PREMIUM = 8,
  PRO_PLUS_NEW = 9,
  ASSET_MANAGER = 10,
  EXPIRED = 11,
  COMMUNITY_ADMIN = 12,
}

export const SUBSCRIPTION_PLAN_DISPLAYS = [
  "Pro",
  "Intermediate",
  "Beginner",
  "Free",
  "Starter",
  "Advanced",
  "Premium",
  "Asset Manager",
] as const;
export const SUBSCRIPTION_PLAN_TYPES = Object.values(
  SubscriptionPlanType
).filter((planType) => !isNaN(Number(planType))) as SubscriptionPlanType[];
export const PURCHASE_SUBSCRIPTION_TYPES = [
  ...SUBSCRIPTION_PLAN_TYPES,
  "Group",
] as const;

export type SubscriptionPlanDisplay =
  (typeof SUBSCRIPTION_PLAN_DISPLAYS)[number];
export type PurchaseSubscriptionType = SubscriptionPlanType | "Group";

export const EXTEND_SUBSCRIPTION_DURATIONS = [30, 360];
export const SUBSCRIPTION_DURATIONS = [30, 90, 180, 360];
export type SubscriptionDuration = (typeof SUBSCRIPTION_DURATIONS)[number];

export interface BasePlanDetails {
  discount?: number;
  price: number;
  subscription_days: SubscriptionDuration;
  subscription_plan_id: number;
  additional_slot_price?: Record<number, number>;
}

export interface PlanDetails extends BasePlanDetails {
  subscription_features: string[] | null;
  subscription_type: SubscriptionPlanDisplay;
  subscription_plan_type: SubscriptionPlanType;
  upgrade_price?: number;
  allow_upgrade?: boolean;
  days_left?: number;
  description?: string;
  image?: string;
  additional_slot_price?: Record<number, number>;
}

export interface BillingAgreementDetails extends BasePlanDetails {
  payer_view_url?: string;
  payment_type: PaymentType;
  sub_group_image?: string;
  sub_group_name?: string;
  upgrade_price?: number;
  subscription_type?: SubscriptionPlanDisplay;
  number_of_clients?: number;
}

export enum PaymentType {
  EXTENSION = 1,
  UPGRADE = 2,
  LICENSES = 3,
  SLOTS = 4,
  PRORATED_SLOTS = 5,
}

export type FormattedPlanDetails = Pick<
  PlanDetails,
  "subscription_features"
> & {
  [key in SubscriptionDuration]?: BasePlanDetails;
} & {
  upgradePlan?: PlanDetails;
  subscription_type?: SubscriptionPlanDisplay;
  subscription_plan_type?: SubscriptionPlanType;
};
export type FormattedPlanDetailsMapping = Record<
  PurchaseSubscriptionType,
  FormattedPlanDetails
>;

export enum SubscriptionPaymentMethodType {
  PAYPAL = 1,
  BTC = 2,
  ETH = 3,
  LTC = 4,
  XRP = 5,
  APPLEPAY = 6,
  USDT_TRC20 = 7,
  USDT_ERC20 = 8,
  USDC_ERC20 = 9,
  USDC_TRC20 = 10,
  GOOGLEPAY_SUBSCRIPTION = 11,
  APPLEPAY_SUBSCRIPTION = 12,
}

export interface PaymentMethod {
  id: SubscriptionPaymentMethodType;
  payment_option: string;
  is_recurring: boolean;
  icon_url: string;
}

interface GetUpgradePaymentLinkRequest {
  payment_option: string;
  payment_type: PaymentType.UPGRADE;
  plan_type: PurchaseSubscriptionType;
  promo_code?: string;
  number_of_clients?: number;
}

export enum SubscriptionType {
  PLATFORM = 1,
  GROUP = 2,
}

interface BaseExtendPaymentLinkRequest {
  payment_option: string;
  payment_type: PaymentType.EXTENSION;
  promo_code?: string;
  number_of_clients?: number;
}

export type GetExtendPaymentLinkRequest = BaseExtendPaymentLinkRequest &
  (
    | {
        subscription_plan_id: number;
      }
    | { group_subscription_plan_id: number; sub_group_id: number }
  );

type GetApiSlotsPaymentLinkRequest = {
  number_of_clients?: number;
  payment_type: PaymentType.SLOTS;
  payment_option: string;
};

type GetProratedApiSlotsPaymentLinkRequest = {
  number_of_clients?: number;
  payment_type: PaymentType.PRORATED_SLOTS;
  payment_option: string;
};

export type GetPaymentLinkRequest = {
  force_invoice?: boolean;
  email: string;
} & (
  | GetUpgradePaymentLinkRequest
  | GetExtendPaymentLinkRequest
  | GetApiSlotsPaymentLinkRequest
  | GetProratedApiSlotsPaymentLinkRequest
);

export interface ConnectEmailRequest {
  email: string;
  password: string;
}

export interface ConnectEmailResponse {
  email_validator_uuid: string;
}

export interface ConfirmEmailConnectionRequest {
  auth_code: string;
  email_validator_uuid: string;
}

export interface PaymentLinkData {
  payment_url: string;
  invoice_id: string;
  time_expires?: number;
}

export enum InvoiceStatus {
  PENDING = 1,
  INACTIVE = 2,
  EXPIRED = 3,
  PAID = 4,
  REVIEW = 5,
  DENIED = 6,
  REFUNDED = 7,
  REVERSED = 8,
  CANCELLED = 9,
}

export interface PaymentLinkStatus {
  payment_url: string;
  guest_payment_url?: string;
  amount?: string;
  payment_address?: string;
  received?: string;
  remaining?: string;
  time_expires?: number;
  qr_url?: string;
  status?: InvoiceStatus;
}

export interface PaymentDetails {
  cost?: number;
  duration: string;
  payer_view_url: string;
  payment_option: string;
  status: string;
  group_name?: string;
  subscription_type?: string;
  action: string;
  paid_datetime?: string;
  paid_timestamp?: number;
  creation_datetime?: string;
  creation_timestamp?: number;
  update_datetime?: string;
  update_timestamp?: number;
}

export interface GetUpgradePricesResponse {
  slots_setup_price: number;
  upgrade_setup_price: number;
  slots_recurring_price: number;
  plan_recurring_price: number;
  existing_number_of_clients: number;
  total_number_of_clients: number;
  base_number_of_clients?: number;
}

export interface GetUpgradePricesRequest {
  number_of_clients?: number;
  plan_type?: SubscriptionPlanType;
  payment_type?: PaymentType.UPGRADE | PaymentType.PRORATED_SLOTS;
  promo_code?: string | null;
}

export type GetNewPaymentPriceResponse = {
  one_time_cost?: number;
  recurring_price?: number;
};

export type NewPaymentPriceResponse = GetNewPaymentPriceResponse & {
  message?: string;
  code?: number;
};

export type PromoCodePlansPricesType = {
  [key in SubscriptionDuration]: NewPaymentPriceResponse;
};

export enum UsageType {
  ACTIVE_TRADE = 1,
  CLIENT = 2,
  AUTO_TRADING_GROUP = 3,
  DCA_BOT = 4,
  GRID_BOT = 5,
  TV_BOT = 6,
  SIGNAL_BOT = 7,
  UI = 8,
  OPTIMIZED_CONFIGURATION = 9,
  AUTO_TRADING = 10,
  MANUAL_TRADE = 11,
  BACKTESTING = 12,
  SECURE_CLIENT_INVITE = 13,
  TRADINGVIEW_SIGNALS_BOT = 14,
  HEDGING_BOT = 15,
}

export type UsageLimitValue = number | boolean | "inf";

export type PlanUsageLimits = {
  [key in UsageType]?: UsageLimitValue;
};

export type PlansUsageLimits = {
  [key in SubscriptionPlanType]?: PlanUsageLimits;
};

export interface AdditionalUsageLimit {
  expiration_timestamp: number;
  quota: number;
  usage_type: UsageType;
}

export type AdditionalUsageLimits = AdditionalUsageLimit[];

export interface GetUsageLimitsResponse {
  additional_limits: AdditionalUsageLimits;
  limits: PlansUsageLimits;
}

export type ApprovePaymentRequest = BaseExtendPaymentLinkRequest & {
  transaction_id: string;
  user_uuid: string;
  product_id: string;
};

export interface ApproveSubscriptionRequest {
  promo_code: string;
  product_id: string;
  original_transaction_id: string;
  transaction_id: string;
  user_uuid: string;
  payment_option: string;
  verification_result_ios?: string;
}

export enum CancellationReason {
  NO_LONGER_TRADING = 1,
  FREE_PLAN_SUFFICIENT = 2,
  SWITCHING_CHEAPER = 3,
  SWITCHING_FEATURES = 4,
  BUDGET_CONSTRAINTS = 5,
  TECHNICAL_ISSUES = 6,
  USABILITY_ISSUES = 7,
  PAUSING_TEMPORARY = 8,
  RENEW_LATER = 9,
  OTHER = 10,
}

export interface CancellationReasonOption {
  id: CancellationReason;
  label: string;
}

export interface SaveCancellationReasonRequest {
  reason_id: CancellationReason;
  additional_text?: string;
}

export interface AssetManagerInvoice {
  id: number;
  period_start: UnixTimestamp;
  period_end: UnixTimestamp;
  total_pnl: number;
  profit_sharing_percent: number;
  profit_share_amount: number;
  payout_method_name: string;
  pdf_url: string;
  status: AssetManagerInvoiceStatus;
  is_visible_to_sub_users: boolean;
  status_display: string;
  created_at: UnixTimestamp;
  updated_at: UnixTimestamp;
  client_ids: number[];
}

export type AssetManagerInvoiceWithKey = AssetManagerInvoice & {
  key: string | number;
};

export type generateAssetManagerInvoiceRequest = {
  period_start: UnixTimestamp;
  period_end: UnixTimestamp;
  profit_sharing_percent: number;
  payout_method_id: number;
  client_ids: number[];
  is_visible_to_sub_users: boolean;
};

export enum PayoutMethodSource {
  AFFILIATION = 1,
  INVOICE = 2,
}

export enum PaymentMethodType {
  PAYPAL = 1,
  CRYPTO = 2,
}

export type CreatePayoutMethodRequest = {
  type: PaymentMethodType;
  name: string;
  source: PayoutMethodSource;
  email?: string;
  asset?: CryptoAsset;
  network?: CryptoNetwork;
  address?: string;
  tag?: string;
};

export type EditPayoutMethodRequest = {
  payout_method_id: number;
  name: string;
};

export interface PayoutMethod {
  time: number;
  payout_method_type: PaymentMethodType;
  name: string;
  payout_method_id: number;
  email?: string;
  address?: string;
  asset?: CryptoAsset;
  network?: CryptoNetwork;
  tag?: string;
  source: PayoutMethodSource;
}

export enum CryptoAsset {
  BTC = 1,
  USDT = 2,
  XRP = 3,
}

export enum CryptoNetwork {
  ETH = 1,
  TRX = 2,
  BTC = 3,
  XRP = 4,
}

export enum AssetManagerInvoiceStatus {
  PENDING = 1,
  PAID = 2,
  CANCELED = 3,
  REFUNDED = 4,
  DELETED = 5,
}

export type updateAssetManagerInvoiceStatus = {
  invoice_id: number;
  status: AssetManagerInvoiceStatus;
};

export type updateAssetManagerInvoiceVisibility = {
  invoice_id: number;
  is_visible_to_sub_users: boolean;
};

export type SetAssetManagerConfigurationRequest = {
  tab_title: string | null;
  display_name: string | null;
  language: string;
  supported_exchanges: string[] | null;
  light_mode_primary_color: string | null;
  dark_mode_primary_color: string | null;
};

export interface AssetManagerConfigurationType {
  light_mode_logo: string;
  dark_mode_logo: string;
  favicon: string;
  tab_title: string;
  cname?: string;
  display_name: string;
  language: string;
  light_mode_display_image: string;
  dark_mode_display_image: string;
  supported_exchanges: string[];
  light_mode_primary_color: string | null;
  dark_mode_primary_color: string | null;
  modified_create_bot_button_texts?: boolean;
  follow_signal_timeout?: number;
  whitelabel?: boolean;
  display_trade_details?: boolean;
  allow_view_only_users_to_view_bots?: boolean;
  hide_quick_connect: boolean | null;
  show_social_login: boolean;
}
